import React, { useState, useMemo, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  HomeIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  DocumentIcon,
  KeyIcon,
  ShoppingCartIcon,
  CalendarIcon,
  GiftIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  ArrowsRightLeftIcon,
  DevicePhoneMobileIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/authStore';
import NotificationsPanel from '../notifications/NotificationsPanel';
import { useFamilyStore } from '../../store/familyStore';
import api from '../../api/client';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function AppLayout() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { currentFamily } = useFamilyStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const { t } = useTranslation();
  const navigateToFamilies = () => navigate('/families');
  const navItems = [
    { path: '/', icon: HomeIcon, label: t('nav.dashboard') },
    { path: '/families', icon: UsersIcon, label: t('nav.families') },
    { path: '/todos', icon: ClipboardDocumentListIcon, label: t('nav.todos') },
    { path: '/documents', icon: DocumentIcon, label: t('nav.documents') },
    { path: '/passwords', icon: KeyIcon, label: t('nav.passwords') },
    { path: '/shopping', icon: ShoppingCartIcon, label: t('nav.shopping') },
    { path: '/calendar', icon: CalendarIcon, label: t('nav.calendar') },
    { path: '/gifts', icon: GiftIcon, label: t('nav.gifts', 'Gifts') },
    { path: '/settings', icon: Cog6ToothIcon, label: t('nav.settings') },
  ];

  const apiBase = useMemo(() => (import.meta.env.VITE_API_URL || '').replace(/\/$/, ''), []);
  const avatarUrl = useMemo(() => {
    if (!user?.avatar_url) return null;
    if (user.avatar_url.startsWith('http')) return user.avatar_url;
    return `${apiBase}${user.avatar_url}`;
  }, [apiBase, user?.avatar_url]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Check for activity count
  useEffect(() => {
    const loadActivityCount = async () => {
      try {
        const res = await api.get('/activities/my-activities', { params: { limit: 20 } });
        const activities = res.data?.activities || [];
        setUnreadCount(activities.length);
      } catch (e) {
        // Ignore errors
      }
    };

    loadActivityCount();
    // Refresh every 30 seconds
    const interval = setInterval(loadActivityCount, 30000);
    
    // Listen for activity updates
    const handleActivityUpdate = (e: CustomEvent) => {
      setUnreadCount(e.detail.unreadCount);
    };
    window.addEventListener('notificationsUpdated', handleActivityUpdate as EventListener);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('notificationsUpdated', handleActivityUpdate as EventListener);
    };
  }, []);

  // Check PWA install status
  useEffect(() => {
    const checkInstallStatus = () => {
      // Check if already installed (standalone mode or in app)
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isInApp = (window.navigator as any).standalone === true; // iOS
      const isInstalledCheck = isStandalone || isInApp;
      
      setIsInstalled(isInstalledCheck);
      
      // If installed, clear deferred prompt
      if (isInstalledCheck) {
        setDeferredPrompt(null);
      }
    };

    // Check immediately
    checkInstallStatus();

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      // Only set if not already installed
      if (!isInstalled) {
        setDeferredPrompt(e as BeforeInstallPromptEvent);
      }
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Re-check periodically (in case status changes)
    const interval = setInterval(checkInstallStatus, 1000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      clearInterval(interval);
    };
  }, [isInstalled]);

  const handleInstallPWA = async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setDeferredPrompt(null);
      }
    } catch (error) {
      console.error('Error showing install prompt:', error);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex md:w-72 md:flex-col md:fixed md:inset-y-0 glass-dark">
        <div className="flex-1 flex flex-col min-h-0 p-4">
          {/* Logo */}
          <div className="flex items-center gap-3 px-3 mb-8">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
              <HomeIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">MyFamily</h1>
              {currentFamily && (
                <p className="text-xs text-surface-400 truncate max-w-[140px]">
                  {currentFamily.name}
                </p>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  isActive ? 'nav-item-active' : 'nav-item'
                }
                end={item.path === '/'}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="pt-4 border-t border-white/10">
            <div className="flex items-center gap-3 px-3 py-2">
              <button className="btn-icon relative" onClick={() => setNotifOpen((v) => !v)}>
                <BellIcon className="w-6 h-6" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </button>
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Avatar"
                  className="w-10 h-10 rounded-full object-cover border border-white/10"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white font-semibold">
                  {user?.first_name?.[0]?.toUpperCase() || 'U'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-surface-400 truncate">
                  @{user?.username}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="btn-icon text-surface-400 hover:text-red-400"
                title={t('nav.logout')}
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="px-3 py-2 space-y-2">
              {!isInstalled && (
                <button
                  onClick={handleInstallPWA}
                  disabled={!deferredPrompt}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <DevicePhoneMobileIcon className="w-5 h-5" />
                  <span>Install the app!</span>
                </button>
              )}
              <button
                onClick={navigateToFamilies}
                className="btn-secondary w-full flex items-center justify-center gap-2"
              >
                <ArrowsRightLeftIcon className="w-5 h-5" />
                <span>{t('families.title')}</span>
              </button>
              {user?.is_superadmin && (
                <NavLink
                  to="/admin"
                  className="btn-secondary w-full flex items-center justify-center gap-2 !bg-amber-500/20 !text-amber-400 !border-amber-500/30 hover:!bg-amber-500/30"
                >
                  <ShieldCheckIcon className="w-5 h-5" />
                  <span>Admin Panel</span>
                </NavLink>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed inset-y-0 left-0 w-72 glass-dark z-50 md:hidden"
            >
              <div className="flex flex-col h-full p-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                      <HomeIcon className="w-6 h-6 text-white" />
                    </div>
                    <h1 className="text-lg font-bold text-white">MyFamily</h1>
                  </div>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="btn-icon"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                {/* Navigation */}
                <nav className="flex-1 space-y-1">
                  {navItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) =>
                        isActive ? 'nav-item-active' : 'nav-item'
                      }
                      end={item.path === '/'}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </nav>

                {/* User section */}
                <div className="pt-4 border-t border-white/10">
                  <div className="flex items-center gap-3 px-3 py-2 mb-2">
                    <button className="btn-icon relative" onClick={() => { setNotifOpen((v) => !v); setSidebarOpen(false); }}>
                      <BellIcon className="w-6 h-6" />
                      {unreadCount > 0 && (
                        <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                      )}
                    </button>
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt="Avatar"
                        className="w-10 h-10 rounded-full object-cover border border-white/10"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white font-semibold">
                        {user?.first_name?.[0]?.toUpperCase() || 'U'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {user?.first_name} {user?.last_name}
                      </p>
                      <p className="text-xs text-surface-400 truncate">
                        @{user?.username}
                      </p>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="btn-icon text-surface-400 hover:text-red-400"
                    >
                      <ArrowRightOnRectangleIcon className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="px-3 py-2 space-y-2">
                    {!isInstalled && (
                      <button
                        onClick={() => { handleInstallPWA(); setSidebarOpen(false); }}
                        disabled={!deferredPrompt}
                        className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <DevicePhoneMobileIcon className="w-5 h-5" />
                        <span>Install the app!</span>
                      </button>
                    )}
                    <button
                      onClick={() => { navigateToFamilies(); setSidebarOpen(false); }}
                      className="btn-secondary w-full flex items-center justify-center gap-2"
                    >
                      <ArrowsRightLeftIcon className="w-5 h-5" />
                      <span>{t('families.title')}</span>
                    </button>
                    {user?.is_superadmin && (
                      <NavLink
                        to="/admin"
                        onClick={() => setSidebarOpen(false)}
                        className="btn-secondary w-full flex items-center justify-center gap-2 !bg-amber-500/20 !text-amber-400 !border-amber-500/30 hover:!bg-amber-500/30"
                      >
                        <ShieldCheckIcon className="w-5 h-5" />
                        <span>Admin Panel</span>
                      </NavLink>
                    )}
                  </div>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="flex-1 md:pl-72">
        {/* Mobile header */}
        <header className="md:hidden sticky top-0 z-30 glass-dark safe-top">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="btn-icon"
            >
              <Bars3Icon className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <HomeIcon className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold truncate max-w-[140px]">
                {currentFamily?.name || 'MyFamily'}
              </span>
            </div>
            <button className="btn-icon relative" onClick={() => setNotifOpen((v) => !v)}>
              <BellIcon className="w-6 h-6" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              )}
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="page">
          <Outlet />
        </main>

        <NotificationsPanel open={notifOpen} onClose={() => setNotifOpen(false)} />

        {/* Mobile bottom navigation */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 glass-dark safe-bottom z-30">
          <div className="flex items-center justify-around py-2">
            {navItems.slice(0, 5).map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all ${
                    isActive
                      ? 'text-primary-400'
                      : 'text-surface-400 hover:text-white'
                  }`
                }
                end={item.path === '/'}
              >
                <item.icon className="w-6 h-6" />
                <span className="text-xs">{item.label.split(' ')[0]}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
    </div>
  );
}

